import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate } from 'react-router-dom';
import { getAllChildren } from '../../services/firestore';
import { useMessaging } from '../../contexts/MessagingContext';
import { FaBirthdayCake, FaVenusMars, FaUserShield, FaAllergies, FaPuzzlePiece } from 'react-icons/fa';

const ageGroups = [
  { value: '', label: 'All Age Groups' },
  { value: 'Infant/Toddler', label: 'Infant/Toddler (6 weeks–29 months)' },
  { value: 'Preschool', label: 'Preschool (30 months–Kindergarten entry)' },
  { value: 'School-Age', label: 'School-Age (Grade 1–13 years)' }
];

const genders = [
  { value: '', label: 'Any' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
  { value: 'Prefer not to say', label: 'Prefer not to say' }
];

const SearchChildren = () => {
  const [children, setChildren] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    ageGroup: '',
    gender: '',
    specialNeeds: ''
  });
  const navigate = useNavigate();
  const { startConversation } = useMessaging();

  useEffect(() => {
    setLoading(true);
    getAllChildren()
      .then(children => setChildren(children))
      .catch(() => setChildren([]))
      .finally(() => setLoading(false));
  }, []);

  // Helper to calculate age from dateOfBirth
  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Filtering logic
  useEffect(() => {
    let filteredList = children;
    if (filters.ageGroup) {
      filteredList = filteredList.filter(child => {
        // If child has ageCategory, use it directly
        if (child.ageCategory) {
          return child.ageCategory === filters.ageGroup;
        }
        // Otherwise calculate age category from dateOfBirth
        const age = calculateAge(child.dateOfBirth);
        const ageCategory = getAgeCategory(age);
        return ageCategory === filters.ageGroup;
      });
    }
    if (filters.gender) {
      filteredList = filteredList.filter(child => child.gender === filters.gender);
    }
    if (filters.specialNeeds) {
      filteredList = filteredList.filter(child => (child.specialNeeds || '').toLowerCase().includes(filters.specialNeeds.toLowerCase()));
    }
    setFiltered(filteredList);
  }, [filters, children]);

  // Helper to get age category
  const getAgeCategory = (age) => {
    if (age === null || age === undefined || isNaN(age)) return 'N/A';
    if (age <= 2) return 'Infant/Toddler';
    if (age >= 3 && age <= 5) return 'Preschool';
    if (age >= 6) return 'School-Age';
    return 'N/A';
  };

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleContactParent = async (parentUid) => {
    try {
      const conversation = await startConversation(parentUid);
      if (conversation && conversation.id) {
        // Navigate to the specific conversation
        navigate(`/messages/${conversation.id}`);
      } else {
        // Fallback to messages page if conversation creation failed
        navigate('/messages');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      setError('Failed to start conversation with parent');
    }
};

  return (
    <div className="search-children">
      <h1>Find Children</h1>
      <div className="filters-container">
        <h2>Search Filters</h2>
        <div className="form-section">
          <div className="form-group">
            <label>Age Group:</label>
            <select name="ageGroup" value={filters.ageGroup} onChange={handleFilterChange}>
              {ageGroups.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Gender:</label>
            <select name="gender" value={filters.gender} onChange={handleFilterChange}>
              {genders.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Special Needs:</label>
            <input
              type="text"
              name="specialNeeds"
              value={filters.specialNeeds}
              onChange={handleFilterChange}
              placeholder="Search by special needs..."
            />
          </div>
        </div>
      </div>
      <div className="results-container">
        <h2>Children Results</h2>
        {loading && <p>Loading children...</p>}
        {error && <div className="error-message">{error}</div>}
        {!loading && filtered.length === 0 && <p>No children found matching your criteria.</p>}
        <table className="children-search-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Age</th>
              <th>Gender</th>
              <th>Age Group</th>
              <th>Special Needs</th>
              <th>Allergies</th>
              <th>Interests</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((child, idx) => (
              <tr key={idx}>
                <td>{child.name || 'Unnamed Child'}</td>
                <td>{calculateAge(child.dateOfBirth)}</td>
                <td>{child.gender || 'N/A'}</td>
                <td>{child.ageCategory || 'N/A'}</td>
                <td>{child.specialNeeds || '-'}</td>
                <td>{child.allergies || '-'}</td>
                <td>{child.interests && child.interests.length > 0 ? child.interests.join(', ') : '-'}</td>
                <td>
                  <button
                    className="contact-parent-button"
                    onClick={() => handleContactParent(child.parentUid)}
                  >
                    Contact Parent
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SearchChildren; 